/**
 * Wrapper pour les opérations Firestore avec gestion d'erreur robuste
 * Fournit des méthodes avec retry automatique et gestion des erreurs
 */

import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  collection, 
  getDocs, 
  query, 
  DocumentReference, 
  CollectionReference, 
  Query,
  DocumentData,
  QuerySnapshot,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../services/firebase';
import firestoreHealthService from '../services/firestoreHealthService';
import { toast } from 'react-toastify';

export interface FirestoreOperationOptions {
  maxRetries?: number;
  retryDelay?: number;
  showErrorToast?: boolean;
  silentMode?: boolean;
}

const DEFAULT_OPTIONS: FirestoreOperationOptions = {
  maxRetries: 3,
  retryDelay: 1000,
  showErrorToast: true,
  silentMode: false
};

class FirestoreWrapper {
  /**
   * Exécute une opération Firestore avec gestion d'erreur et retry
   */
  private static async executeWithRetry<T>(
    operation: () => Promise<T>,
    operationName: string,
    options: FirestoreOperationOptions = {}
  ): Promise<T> {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    let lastError: any;
    
    for (let attempt = 1; attempt <= opts.maxRetries!; attempt++) {
      try {
        if (!opts.silentMode && attempt > 1) {
          // Tentative de reconnexion
        }
        
        const result = await operation();
        
        if (attempt > 1 && !opts.silentMode) {
          // Opération réussie
        }
        
        return result;
        
      } catch (error: any) {
        lastError = error;
        
        if (!opts.silentMode) {
          console.warn(`⚠️ ${operationName} - Erreur tentative ${attempt}:`, error.message);
        }
        
        // Vérifier si c'est une erreur de connectivité
        if (this.isConnectivityError(error)) {
          // Déclencher une vérification de santé
          firestoreHealthService.checkFirestoreHealth();
          
          // Attendre avant de retry si ce n'est pas la dernière tentative
          if (attempt < opts.maxRetries!) {
            await this.delay(opts.retryDelay! * attempt); // Backoff progressif
            continue;
          }
        } else {
          // Pour les erreurs non liées à la connectivité, ne pas retry
          break;
        }
      }
    }
    
    // Toutes les tentatives ont échoué
    if (opts.showErrorToast && !opts.silentMode) {
      const errorMessage = this.getErrorMessage(lastError);
      toast.error(`Erreur ${operationName}: ${errorMessage}`);
    }
    
    throw lastError;
  }
  
  /**
   * Vérifie si l'erreur est liée à la connectivité
   */
  private static isConnectivityError(error: any): boolean {
    const errorCode = error.code || '';
    const errorMessage = error.message || '';
    
    return (
      errorCode.includes('unavailable') ||
      errorCode.includes('deadline-exceeded') ||
      errorCode.includes('cancelled') ||
      errorMessage.includes('ERR_ABORTED') ||
      errorMessage.includes('network') ||
      errorMessage.includes('timeout')
    );
  }
  
  /**
   * Extrait un message d'erreur lisible
   */
  private static getErrorMessage(error: any): string {
    if (error.code === 'permission-denied') {
      return 'Permissions insuffisantes';
    }
    if (error.code === 'not-found') {
      return 'Document non trouvé';
    }
    if (this.isConnectivityError(error)) {
      return 'Problème de connexion';
    }
    return error.message || 'Erreur inconnue';
  }
  
  /**
   * Délai d'attente
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  // ========================================
  // MÉTHODES PUBLIQUES
  // ========================================
  
  /**
   * Lecture d'un document avec retry
   */
  static async getDocument(
    docRef: DocumentReference,
    options?: FirestoreOperationOptions
  ): Promise<DocumentSnapshot<DocumentData>> {
    return this.executeWithRetry(
      () => getDoc(docRef),
      'Lecture document',
      options
    );
  }
  
  /**
   * Écriture d'un document avec retry
   */
  static async setDocument(
    docRef: DocumentReference,
    data: DocumentData,
    options?: FirestoreOperationOptions
  ): Promise<void> {
    return this.executeWithRetry(
      () => setDoc(docRef, data),
      'Écriture document',
      options
    );
  }
  
  /**
   * Mise à jour d'un document avec retry
   */
  static async updateDocument(
    docRef: DocumentReference,
    data: Partial<DocumentData>,
    options?: FirestoreOperationOptions
  ): Promise<void> {
    return this.executeWithRetry(
      () => updateDoc(docRef, data),
      'Mise à jour document',
      options
    );
  }
  
  /**
   * Suppression d'un document avec retry
   */
  static async deleteDocument(
    docRef: DocumentReference,
    options?: FirestoreOperationOptions
  ): Promise<void> {
    return this.executeWithRetry(
      () => deleteDoc(docRef),
      'Suppression document',
      options
    );
  }
  
  /**
   * Lecture d'une collection avec retry
   */
  static async getCollection(
    queryRef: Query<DocumentData> | CollectionReference<DocumentData>,
    options?: FirestoreOperationOptions
  ): Promise<QuerySnapshot<DocumentData>> {
    return this.executeWithRetry(
      () => getDocs(queryRef),
      'Lecture collection',
      options
    );
  }
  
  /**
   * Helpers pour créer des références
   */
  static docRef(path: string, ...pathSegments: string[]): DocumentReference {
    return doc(db, path, ...pathSegments);
  }
  
  static collectionRef(path: string, ...pathSegments: string[]): CollectionReference {
    return collection(db, path, ...pathSegments);
  }
}

export default FirestoreWrapper;